import { create } from 'zustand';
import { toast } from 'sonner';

const saveToStorage = (items) => {
  localStorage.setItem('cart-storage', JSON.stringify({ state: { items } }));
};

const newLineId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Stock for the chosen colour (if it tracks stock), else product stock. Missing = 0.
export const availableStock = (product, colorName) => {
  const variant = colorName ? (product?.color_variants || []).find((v) => v.color_name === colorName) : null;
  const stock = variant && variant.stock != null ? variant.stock : product?.stock;
  return Number(stock) > 0 ? Number(stock) : 0;
};

const qtyInCart = (items, productId, colorName, except = null) =>
  items
    .filter((i) => i !== except && i.product_id === productId && (i.selected_color || null) === (colorName || null))
    .reduce((n, i) => n + i.quantity, 0);

const matchesKey = (item, key) => item.line_id === key || (!item.line_id && item.product_id === key);

const useCartStore = create((set, get) => ({
  items: [],
  // Returns false (and shows a hint) when the item cannot be added.
  addItem: (product, quantity = 1, selectedColor = null, finalPrice = null, personalisation = null) => {
    const items = get().items;
    const colorName = selectedColor?.color_name || null;
    if ((product.color_variants || []).length > 0 && !colorName) {
      toast.info('Bitte wähle auf der Produktseite zuerst eine Farbe aus.');
      return false;
    }
    const available = availableStock(product, colorName);
    const inCart = qtyInCart(items, product.id, colorName);
    if (available <= 0) {
      toast.error(`„${product.name}“ ist leider ausverkauft.`);
      return false;
    }
    if (inCart + quantity > available) {
      toast.error(`Nur noch ${available} Stück verfügbar${inCart ? ` – ${inCart} liegen schon im Warenkorb` : ''}.`);
      return false;
    }
    const onSale = product.sale_price && product.sale_price < product.price;
    const price = finalPrice || (onSale ? product.sale_price : product.price);
    const giftLabelText = personalisation?.gift_label_text || null;
    const giftNote = personalisation?.gift_note || null;

    // Personalised items always become a new line so each gift keeps its own label text.
    const isPersonalised = !!(giftLabelText || giftNote);

    const existingItem = !isPersonalised
      ? items.find(
          (item) =>
            item.product_id === product.id &&
            item.selected_color === colorName &&
            !item.gift_label_text &&
            !item.gift_note,
        )
      : null;

    let newItems;
    if (existingItem) {
      newItems = items.map((item) =>
        item === existingItem ? { ...item, quantity: item.quantity + quantity, line_id: item.line_id || newLineId() } : item,
      );
    } else {
      newItems = [
        ...items,
        {
          line_id: newLineId(),
          product_id: product.id,
          quantity,
          price,
          selected_color: colorName,
          gift_label_text: giftLabelText,
          gift_note: giftNote,
          product,
        },
      ];
    }

    set({ items: newItems });
    saveToStorage(newItems);
    return true;
  },
  // key = line_id (older carts: product_id)
  updateQuantity: (key, quantity) => {
    const items = get().items;
    const line = items.find((i) => matchesKey(i, key));
    if (!line) return;
    let newItems;
    if (quantity <= 0) {
      newItems = items.filter((i) => i !== line);
    } else {
      const max = availableStock(line.product, line.selected_color) - qtyInCart(items, line.product_id, line.selected_color, line);
      if (quantity > max) {
        toast.error(max > 0 ? `Nur noch ${max} Stück verfügbar.` : 'Keine weiteren Stück verfügbar.');
        if (max <= 0 || line.quantity >= max) return;
        quantity = max;
      }
      newItems = items.map((i) => (i === line ? { ...i, quantity } : i));
    }
    set({ items: newItems });
    saveToStorage(newItems);
  },
  removeItem: (key) => {
    const items = get().items;
    const line = items.find((i) => matchesKey(i, key));
    const newItems = items.filter((i) => i !== line);
    set({ items: newItems });
    saveToStorage(newItems);
  },
  clearCart: () => {
    set({ items: [] });
    saveToStorage([]);
  },
  getTotal: () => {
    return get().items.reduce((total, item) => total + item.price * item.quantity, 0);
  },
  getItemCount: () => {
    return get().items.reduce((count, item) => count + item.quantity, 0);
  },
  // Hydrate from localStorage
  hydrate: () => {
    const stored = localStorage.getItem('cart-storage');
    if (stored) {
      try {
        const { state } = JSON.parse(stored);
        const items = (state?.items || []).map((i) => (i.line_id ? i : { ...i, line_id: newLineId() }));
        set({ ...state, items });
      } catch (e) {
        console.error('Failed to parse cart storage', e);
      }
    }
  },
}));

// Hydrate on init
if (typeof window !== 'undefined') {
  useCartStore.getState().hydrate();
}

export default useCartStore;
